import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {RadioButton} from "primereact/radiobutton";
import listIcon from "../../../../../resources/images/splitmodes/list.png";
import lastIcon from "../../../../../resources/images/splitmodes/last.png";
import firstIcon from "../../../../../resources/images/splitmodes/first.png";
import {InputNumber} from "primereact/inputnumber";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import SplitDialogFields from "../../field/page/SplitDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	SplitMode,
	ToolboxSplitSplit,
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface SplitDialogStatus extends PortalDialogState<ToolboxSplitSplit> {
	pages: number;
	currentPage: string;
}

export class SplitDialogDefinition extends PortalDialog<ToolboxSplitSplit, PortalDialogProperties<ToolboxSplitSplit>,
	SplitDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<SplitDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.splitDocument, Functions.getSplitDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.SPLIT, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxSplitSplit | undefined = this.state.parameter?.data;
		let errors: Map<SplitDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={!this.state.fields?.contains(SplitDialogFields.MODE)}>
					<div className="p-col-12 p-grid" hidden={!this.state.fields?.contains(SplitDialogFields.MODE_LAST)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-mode-last">
							<Translate {...this.props} data="split.mode.last.label" namespace="dialog"/>
							<Info {...this.props} data="split.mode.last.description" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 radio-container">
							<RadioButton inputId="control-mode-last" value={SplitMode.Last} name="mode"
										 onChange={(e) => this.onChange(
											 SplitDialogFields.MODE_LAST, e.value
										 )}
										 checked={parameter?.mode === SplitMode.Last}/>
							<label htmlFor="control-mode-last" className="p-radiobutton-label">
								<img src={lastIcon} style={{marginTop: "-5px"}}
									 alt={Translate.value("split.mode.last.alt", {ns: "dialog"})}/>
							</label>
						</div>
					</div>

					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(SplitDialogFields.MODE_FIRST)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-mode-first">
							<Translate {...this.props} data="split.mode.first.label" namespace="dialog"/>
							<Info {...this.props} data="split.mode.first.description" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 radio-container">
							<RadioButton inputId="control-mode-first" value={SplitMode.First} name="mode"
										 onChange={(e) => this.onChange(
											 SplitDialogFields.MODE_FIRST, e.value
										 )}
										 checked={parameter?.mode === SplitMode.First}/>
							<label htmlFor="control-mode-first" className="p-radiobutton-label">
								<img src={firstIcon} style={{marginTop: "-5px"}}
									 alt={Translate.value("split.mode.first.alt", {ns: "dialog"})}/>
							</label>
						</div>
					</div>

					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(SplitDialogFields.MODE_LIST)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-mode-list">
							<Translate {...this.props} data="split.mode.list.label" namespace="dialog"/>
							<Info {...this.props} data="split.mode.list.description" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8 radio-container">
							<RadioButton inputId="control-mode-list" value={SplitMode.List} name="mode"
										 onChange={(e) => this.onChange(
											 SplitDialogFields.MODE_LIST, e.value
										 )}
										 checked={parameter?.mode === SplitMode.List}/>
							<label htmlFor="control-mode-list" className="p-radiobutton-label">
								<img src={listIcon} style={{marginTop: "-5px"}}
									 alt={Translate.value("split.mode.list.alt", {ns: "dialog"})}/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(SplitDialogFields.PAGERANGE) ||
							 parameter?.mode !== SplitMode.List
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-pagerange">
							<Translate {...this.props} data="split.pages.label" namespace="dialog"/>
							<Info {...this.props} data="split.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<PageRange inputId="control-pagerange"
									   className={errors.has(SplitDialogFields.PAGERANGE) ? " p-invalid" : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.pages, ToolboxSplitSplit.getPagesDefault()
									   )}
									   onChange={(e) => this.onChange(
										   SplitDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")}
									   addon={Translate.value(
										   "inputs.pagerange.of", {pages: this.state.pages}
									   )}
									   tooltip={errors.has(SplitDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(SplitDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(SplitDialogFields.PAGERANGE) ?
								Translate.value(
									errors.get(SplitDialogFields.PAGERANGE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>

					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(SplitDialogFields.PAGENUMBER) ||
						parameter?.mode === SplitMode.List
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-pagenumber"
							   hidden={parameter?.mode !== SplitMode.First}>
							<Translate {...this.props} data="split.start_page.from" namespace="dialog"/>
						</label>
						<label className="p-col-12 p-sm-4" htmlFor="control-pagenumber"
							   hidden={parameter?.mode !== SplitMode.Last}>
							<Translate {...this.props} data="split.start_page.to" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputNumber inputId="control-pagenumber" step={1} showButtons={true} useGrouping={false}
										 value={FunctionHelper.applyValue(
											 parameter?.startPage, ToolboxSplitSplit.getStartPageDefault()
										 )}
										 min={ToolboxSplitSplit.getStartPageMin()} max={this.state.pages}
										 onChange={(e) => this.onChange(
											 SplitDialogFields.PAGENUMBER, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["split-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.split.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.split.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: SplitDialogFields, value: any): void {
		let parameter: ToolboxSplitSplit = this.state.parameter!.data;
		let errors: Map<SplitDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case SplitDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.pages = value;
				break;
			case SplitDialogFields.MODE_LIST:
				this.onChange(SplitDialogFields.PAGERANGE, parameter.pages);
				parameter.mode = SplitMode.List;
				break;
			case SplitDialogFields.MODE_FIRST:
				if (errors.has(SplitDialogFields.PAGERANGE)) {
					errors.delete(SplitDialogFields.PAGERANGE);
				}

				parameter.mode = SplitMode.First;
				break;
			case SplitDialogFields.MODE_LAST:
				if (errors.has(SplitDialogFields.PAGERANGE)) {
					errors.delete(SplitDialogFields.PAGERANGE);
				}

				parameter.mode = SplitMode.Last;
				break;
			case SplitDialogFields.PAGENUMBER:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ToolboxSplitSplit.getStartPageDefault();
				}

				parameter.startPage = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-split small"}/>
					<Translate {...this.props} data="split.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canAssemble, Security.getCanAssembleDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxSplitSplit | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		parameter!.startPage = documentSettings.activeViewer.currentPage;
		parameter!.pages = documentSettings.pageSelection.toString(pageCount, documentSettings.activeViewer.currentPage);

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let parameter: ToolboxSplitSplit = this.state.parameter!.data;
		if (parameter.mode !== SplitMode.List) {
			parameter.pages = parameter.startPage!.toString();
		}

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			split: parameter.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.split", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.SPLIT;
	}

	protected getDefaultParameter(): Parameter<ToolboxSplitSplit> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const SplitDialog = React.memo(SplitDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxSplitSplit>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxSplitSplit>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default SplitDialog;